import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import SEO from "../SEO";
import { UserPreferencesProvider, useUserPreferences } from "../../context/UserPreferencesContext";
import AdminSidebar from "./workspace/AdminSidebar";
import AdminTopbar from "./workspace/AdminTopbar";
import EnterpriseCommandPalette, { useCommandPaletteShortcut } from "./workspace/EnterpriseCommandPalette";
import { useNavigationRegistry } from "./workspace/useNavigationRegistry";
import { NAV_ITEMS_FLAT, resolveBreadcrumbs } from "./workspace/nav-config";

// Backwards-compat re-export — sommige oude modules importeren dit direct.
// De nieuwe waarheid is `nav-config.js` (Enterprise Workspace).
export const ADMIN_MENU = Object.values(NAV_ITEMS_FLAT).map(({ item, group }) => ({
  key: item.key,
  label: item.label,
  icon: item.icon,
  perm: item.perm,
  group: group.key,
}));

function AdminWorkspaceInner({ active, setActive, children }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { recordVisit } = useUserPreferences();
  const navRegistry = useNavigationRegistry();

  useCommandPaletteShortcut(() => setSearchOpen(true));

  // Track visits — elke keer dat "active" wijzigt, log het.
  useEffect(() => {
    if (!active) return;
    const entry = NAV_ITEMS_FLAT[active];
    if (entry) {
      recordVisit({
        key: active,
        label: entry.item.label,
      });
    }
  }, [active, recordVisit]);

  const crumbs = useMemo(() => resolveBreadcrumbs(active), [active]);

  // Sprint A3 — detect current entity from query-string voor
  // context-aware actions in de Command Palette.
  const [activeEntityInfo, setActiveEntityInfo] = useState({ type: null, id: null });
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("customer_id") ? { type: "customer", id: params.get("customer_id") }
      : params.get("invoice_id") ? { type: "invoice", id: params.get("invoice_id") }
      : params.get("booking_id") ? { type: "booking", id: params.get("booking_id") }
      : params.get("shipment_id") ? { type: "shipment", id: params.get("shipment_id") }
      : { type: null, id: null };
    setActiveEntityInfo(t);
  }, [active]);

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const handleSearchNavigate = (key) => {
    if (key) setActive(key);
    setSearchOpen(false);
  };

  const handleQuickAction = (a) => {
    if (a.to) setActive(a.to);
  };

  const handleBreadcrumbNavigate = (key) => {
    if (key) setActive(key);
  };

  return (
    <>
      <SEO title="Admin Backoffice | 5999Cargo" description="Interne admin-omgeving" />
      <div className="min-h-screen bg-slate-50 flex" data-testid="admin-shell">
        {mobileOpen && (
          <div
            className="fixed inset-0 z-30 lg:hidden bg-slate-900/50 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
            aria-hidden
          />
        )}
        <AdminSidebar
          active={active}
          setActive={setActive}
          onLogout={handleLogout}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
        />

        <div className="flex-1 flex flex-col min-w-0">
          <AdminTopbar
            crumbs={crumbs}
            onNavigate={handleBreadcrumbNavigate}
            onOpenSearch={() => setSearchOpen(true)}
            onOpenMobileMenu={() => setMobileOpen(true)}
            onLogout={handleLogout}
            onQuickAction={handleQuickAction}
          />
          <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-x-auto" data-testid="admin-main">
            {children}
          </main>
        </div>
      </div>
      <EnterpriseCommandPalette
        open={searchOpen}
        onOpenChange={setSearchOpen}
        activeSection={active}
        activeEntityType={activeEntityInfo.type}
        activeEntityId={activeEntityInfo.id}
        navigation={{ groups: navRegistry.groups, quick_actions: navRegistry.quick_actions }}
        onNavigate={handleSearchNavigate}
      />
    </>
  );
}

export default function AdminLayout({ active, setActive, children }) {
  return (
    <UserPreferencesProvider>
      <AdminWorkspaceInner active={active} setActive={setActive}>
        {children}
      </AdminWorkspaceInner>
    </UserPreferencesProvider>
  );
}
